/**
 * DateJumpPicker — shared date-jump control with a pill trigger + mini-calendar popover.
 * Iter-18: visual family with PeriodSelector (Dashboard "WED, JUL 15, 2026" chip look).
 */
import React, { useState, useRef, useEffect } from 'react';

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function formatPillLabel(date) {
  return date.toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
  }).toUpperCase();
}
function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function getMonthGrid(year, month) {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const startPad = first.getDay();
  const days = [];
  for (let i = startPad - 1; i >= 0; i--) days.push({ date: new Date(year, month, -i), currentMonth: false });
  for (let d = 1; d <= last.getDate(); d++) days.push({ date: new Date(year, month, d), currentMonth: true });
  while (days.length < 42) {
    const next = new Date(days[days.length - 1].date);
    next.setDate(next.getDate() + 1);
    days.push({ date: next, currentMonth: false });
  }
  return days;
}

export default function DateJumpPicker({ value, onChange, label = 'Jump to date', align = 'left' }) {
  const [open, setOpen] = useState(false);
  const anchorDate = value instanceof Date && !isNaN(value) ? value : new Date();
  const [viewYear, setViewYear] = useState(anchorDate.getFullYear());
  const [viewMonth, setViewMonth] = useState(anchorDate.getMonth());
  const ref = useRef(null);

  useEffect(() => {
    if (open) {
      const d = value instanceof Date && !isNaN(value) ? value : new Date();
      setViewYear(d.getFullYear());
      setViewMonth(d.getMonth());
    }
  }, [open, value]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const displayLabel = value instanceof Date && !isNaN(value) ? formatPillLabel(value) : formatPillLabel(new Date());
  const grid = getMonthGrid(viewYear, viewMonth);
  const today = new Date();

  const prevMonth = () => setViewMonth(m => { if (m === 0) { setViewYear(y => y - 1); return 11; } return m - 1; });
  const nextMonth = () => setViewMonth(m => { if (m === 11) { setViewYear(y => y + 1); return 0; } return m + 1; });
  const handlePick = (date) => { if (typeof onChange === 'function') onChange(date); setOpen(false); };

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        data-portica-role="date-jump-picker"
        style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', height: 'var(--d-control-h)',
          border: '1px solid var(--color-border)', borderRadius: 'var(--btn-radius)',
          background: 'var(--color-surface)', fontSize: 'var(--d-font-ui)', fontWeight: 500,
          color: 'var(--color-text-default)', cursor: 'pointer',
        }}
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <rect x="1" y="3" width="14" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none" />
          <path d="M1 7h14" stroke="currentColor" strokeWidth="1.5" />
          <path d="M5 1v4M11 1v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
        <span>{displayLabel}</span>
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
          <path d="M2 4l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Pick a date"
          style={{
            position: 'absolute', top: '100%', [align === 'right' ? 'right' : 'left']: 0,
            marginTop: 4, background: 'var(--color-surface)', border: '1px solid var(--neutral-200)',
            borderRadius: 'var(--card-radius)', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            zIndex: 50, width: 264, padding: 8,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4, padding: '0 4px' }}>
            <button type="button" onClick={prevMonth} aria-label="Previous month"
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, borderRadius: 4 }}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M10 4l-4 4 4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-default)' }}>
              {MONTHS[viewMonth]} {viewYear}
            </span>
            <button type="button" onClick={nextMonth} aria-label="Next month"
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, borderRadius: 4 }}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0, padding: '0 4px', marginBottom: 2 }}>
            {DAYS_OF_WEEK.map(d => (
              <div key={d} style={{ textAlign: 'center', fontSize: 10, fontWeight: 600, color: 'var(--color-text-secondary)', paddingBottom: 4 }}>
                {d.charAt(0)}
              </div>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, padding: '0 4px' }}>
            {grid.map(({ date, currentMonth }, i) => {
              const isSelected = value && isSameDay(date, value);
              const isTodayCell = isSameDay(date, today);
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => handlePick(date)}
                  style={{
                    height: 30, fontSize: 12, fontWeight: isSelected ? 600 : 400,
                    color: isSelected ? 'var(--white)' : (currentMonth ? 'var(--color-text-default)' : 'var(--color-text-muted)'),
                    background: isSelected ? 'var(--color-action-primary)' : (isTodayCell ? 'var(--neutral-100)' : 'transparent'),
                    border: 'none', borderRadius: 4, cursor: 'pointer',
                  }}
                >{date.getDate()}</button>
              );
            })}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6, padding: '4px 4px 0', borderTop: '1px solid var(--neutral-100)' }}>
            <button type="button" onClick={() => handlePick(new Date())}
              style={{ fontSize: 12, fontWeight: 500, color: 'var(--color-action-primary)', background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px 6px' }}>
              Jump to today
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
